import type { ComponentType, FC } from 'react';
import { memo, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import useElementSize from '../../hooks/useElementSize';
import type { LayoutBlock } from '../../types/layout';
import { Block } from './Block';
import { isBlockToDisplayOnDesktop, isBlockToDisplayOnMobile } from './blockCheck';
import { type LayoutContainerProps } from './layoutContainers';

type AreaBlocksProps = Partial<LayoutBlocksProps> & {
  container: ComponentType<LayoutContainerProps>;
  containerClassName?: string;
  shouldDisplaySlidableContainer?: boolean;
};

type LayoutBlocksProps = {
  layoutContent: LayoutBlock[];
  isScrollableItself?: boolean;
};

export const AreaBlocks: FC<AreaBlocksProps> = memo(function AreaBlocks(props) {
  let {
    layoutContent = [],
    container: Cmp,
    containerClassName,
    shouldDisplaySlidableContainer,
    ...blockProps
  } = props;

  // Hide area if all blocks are hidden on mobile or desktop
  const shouldHideAreaOnMobile = layoutContent.every(block => !isBlockToDisplayOnMobile(block));
  const shouldHideAreaOnDesktop = layoutContent.every(block => !isBlockToDisplayOnDesktop(block));
  const shouldHideArea =
    layoutContent.length === 0 || (shouldHideAreaOnMobile && shouldHideAreaOnDesktop);

  return !shouldHideArea ? (
    <Cmp
      className={twMerge(
        containerClassName,
        shouldHideAreaOnMobile && '_md:hidden',
        shouldHideAreaOnDesktop && 'md:hidden',
      )}
      area={layoutContent?.[0]?.locate}
      {...(shouldDisplaySlidableContainer && {
        isSlidableContainer: shouldDisplaySlidableContainer,
      })}
    >
      <LayoutBlocks layoutContent={layoutContent} {...blockProps} />
    </Cmp>
  ) : null;
});

export const LayoutBlocks = ({ layoutContent, isScrollableItself }: LayoutBlocksProps) => {
  const { height: stickyOffset } = useElementSize('NavbarHeader');
  const hasStickyContent = layoutContent?.some(block => block.sticky);

  const shouldBeSticky = useMemo(
    () => hasStickyContent || isScrollableItself,
    [hasStickyContent, isScrollableItself],
  );

  const stickyStyles = useMemo(
    () => ({
      top: `${stickyOffset || 0}px`,
      ...(isScrollableItself && { maxHeight: `calc(100vh - ${stickyOffset}px)` }),
    }),
    [isScrollableItself, stickyOffset],
  );

  return (
    <div
      className={twMerge(
        'LayoutBlocks',
        shouldBeSticky && 'md:sticky md:z-10',
        isScrollableItself && 'use-magic-scrollbar',
      )}
      style={stickyStyles}
    >
      {layoutContent?.map(block => (
        <Block key={block?._id} block={{ ...block, sticky: false }} />
      ))}
    </div>
  );
};
